// #POW-80
import { useContext, useEffect, useState } from "react";
import AuthContext from "../context/AuthContext";
import DashboardLayout from "../layouts/DashboardLayout";
import { Alert, AlertColor, Box, Button, CircularProgress, Snackbar, SnackbarCloseReason, Stack, Typography } from "@mui/material";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'; // #POW-20
import {GridFilterModel, GridValidRowModel } from '@mui/x-data-grid';  // #POW-6 > #POW-30
import LogisticTransactionsDataGrid from "../components/LogisticTransactionsDataGrid"; // #POW-20
import DialogCheckInTransaction from "../components/dialogs/DialogCheckInTransaction"; // #POW-21
import { useTranslation } from "react-i18next"; // #POW-29
import FileDownloadIcon from '@mui/icons-material/FileDownload'; // #POW-30
import Config from '../Config'; // #POW-30
import useCustomFetch from '../hooks/useCustomFetch';  // #POW-30
import { blue } from "@mui/material/colors"; // #POW-30
import { useNavigate } from "react-router-dom";
import UnarchiveIcon from '@mui/icons-material/Unarchive'; // #POW-100
import React from "react"; // #POW-100

const AllTransactionsPage = () => {
  const { t } = useTranslation();  // #POW-29
  const customFetch = useCustomFetch(); // #POW-30
  const auth = useContext(AuthContext)
  const navigate = useNavigate();
  
  const { isReady } = useContext(AuthContext)
  const [selectedRows, setSelectedRows] = useState<GridValidRowModel[]>([]); // #POW-6 rows selected in datagrid
  const [selectedLogisticRows, setSelectedLogistcRows] = useState<GridValidRowModel[]>([]); // #POW-21 rows selected in datagrid
  const [open, setOpen] = useState(false);
  const [dataGridChanged, setDataGridChanged] = useState(false); // #POW-28
  const [logisticDataGridChanged, setLogisticDataGridChanged] = useState(false); // #POW-52
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] }); // #POW-30
  const [downloadExcelLoading, setDownloadExcelLoading] = useState(false);  // #POW-30
  const [downloadArchiveLoading, setDownloadArchiveLoading] = useState(false);  // #POW-100
  const [openAlert, setOpenAlert ] = useState(false) // #POW-100
  const [alertMessage, setAlertMessage ] = useState("") // #POW-100
  const [alertSeverity, setAlertSeverity ] = useState<AlertColor | undefined>(undefined) // #POW-100

  useEffect(() => {
    if (isReady) {
      if (!auth.canAccessContext('pages.AllTransactionsPage')) { // #POW-22 
        navigate("/dashboard");
      }
    }
  }, [isReady]);

  // #POW-21 callback function for get data from LogisticTransactionsDataGrid component
  const handleSelectedLogisticRowsChange = (rows:any) => {
    setSelectedLogistcRows(rows);
  }

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseSnackBar = (  // #POW-100
      event: React.SyntheticEvent | Event,
      reason?: SnackbarCloseReason,
    ) => {
      if (reason === 'clickaway') {
        return;
      }
  
      setOpenAlert(false);
    };
    
  const handleClose = () => {
    setOpen(false);
  };

  const handleRefreshLogisticDataGrid = (value:boolean) => { // #POW-52
    setLogisticDataGridChanged(value)
  }

  // #POW-30 callback function for get data from ShopTransactionsDataGrid component
  const handleFilterModelChange = (model:any) => {
    setFilterModel(model);
  }

  // #POW-100
  const exportArchive = async () => {
    setDownloadArchiveLoading(true);
    let selectedRowsToExport: string[] = [];
    if(selectedLogisticRows.length > 0)
    {
      selectedLogisticRows.forEach((item) => selectedRowsToExport.push(item.id));
    }
    // columns to export
    const columns: string[] = [
      'number', // is our id in datagrid
      'transaction_date',  // is our date in datagrid
      'logistic_custom_sku_transfer', 
      'shop_code', 
      'shop_name', 
      'logistic_custom_shipping_code', 
      'logistic_shipping_date', 
      'type', 
      'device_name', 
      'device_imei',
      'device_sn' // is our device_serial in datagrid
    ];

    // #POW-131 additional columns for export (data are in payload json field)
    const extra_columns: string[] = [
      'device_storage',
      'campaign_name',
      'campaign_price',
      'new_device_imei',
      'employee_id', // #POW-133
      'customer_email', // #POW-140
      'customer_phone' // #POW-140
    ];

    //console.log("selectedRowsToExport ",selectedRowsToExport)
    const payload = {
      "transactions": selectedRowsToExport,
      "columns": columns,
      "extra_columns": extra_columns, // #POW-131
      "page-context": "all-transactions-page" // for information only, not used in export
    }
    
    const url = `${Config.serverUrl}/api/logistic/transactions/create-job-task`;
    const response = await customFetch(url, {
      method: 'POST',
      headers: {
      'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: "export-excel-and-pdf-archive",
        payload: payload
        //user: auth.user?.email,
      }),
    });

    if (response.ok) {
      setOpenAlert(true)
      setAlertMessage(t('exportArchive.exportSuccess'))
      setAlertSeverity("success")
    } else {
      setOpenAlert(true)
      setAlertMessage(t('exportArchive.exportError'))
      setAlertSeverity("error")
    }

    setDownloadArchiveLoading(false);
  }

  // #POW-30
  const exportLogisticTransactions = async () => {
    setDownloadExcelLoading(true); // #POW-23
    let filters: { field: string; value: string }[] = [];

    // map datagrid field names to db names
    const fieldMapping: { [key: string]: string } = {
      id: "number", // the id field is the number of transaction
      date: "transaction_date", // the transaction_date field is the date of transaction
      device_serial: "device_sn", // is our device_serial in datagrid
    };
    
    filterModel.items.forEach((item) => {
      const field = fieldMapping[item.field] || item.field;

      filters.push({'field': field, 'value': item.value});
    });

    // columns to export
    const columns: string[] = [
      'number', // is our id in datagrid
      'transaction_date',  // is our date in datagrid
      'logistic_custom_sku_transfer', 
      'shop_code', 
      'shop_name', 
      'logistic_custom_shipping_code', 
      'logistic_shipping_date', 
      'type', 
      'device_name', 
      'device_imei',
      'device_sn' // is our device_serial in datagrid
    ];
  
    let selectedRowsToExport: string[] = []; // #POW-39
    if(selectedLogisticRows.length > 0)  // #POW-39
    {
      selectedLogisticRows.forEach((item) => selectedRowsToExport.push(item.id));
    }

    const url = `${Config.serverUrl}/api/trade-in/transactions/excel`;
    const response = await customFetch(url, {
      method: 'POST',
      headers: {
      'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'logistic-all-transactions',
        rows: selectedRowsToExport,  // #POW-39
        filters: filters,
        columns: columns
      }),
    });

    if (response.ok) {
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `transactions.xlsx`;
      link.click();
      setDownloadExcelLoading(false);
    } else {
        console.error('Failed to download excel file');
    }
  }

  return (
    <>
    <DashboardLayout>
      <Typography component="h1" sx={{textAlign:'left', width:'100%', typography: { xs: 'h5', sm: 'h4' }}}>
        {t('allTransactions.title')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}> {/* #POW-100 */}
        <Button  onClick={() => {
            handleClickOpen();
          }}
          variant="contained" startIcon={<AssignmentTurnedInIcon />} disabled={selectedLogisticRows.length == 0}>
          {t('allTransactions.btnCheckIn')}
        </Button>
        {/* #POW-30 */}
        <Stack direction="row"
          sx={{
            justifyContent: {xs:'center', sm: 'end'},
            alignItems: 'center',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            width: { xs: '100%', sm: 'auto' },
            mt: { xs: 2, sm: 0 }
          }}
        > {/* #POW-100 */}
          <Box sx={{ m: 1, position: 'relative' }}>
            <Button  onClick={() => {
                exportLogisticTransactions();
              }} 
              disabled={downloadExcelLoading}
              variant="contained" startIcon={<FileDownloadIcon />}>{t("transactions.btnExportTransactions")}
            </Button>
            {downloadExcelLoading && ( // #POW-30
                <CircularProgress
                    size={24}
                    sx={{
                    color: blue[900],
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                    }}
                />
            )}
          </Box>
          <Box sx={{ m: 1, position: 'relative' }}> {/* #POW-100 */}
            <Button  onClick={() => {
                exportArchive();
              }} 
              disabled={downloadArchiveLoading || selectedLogisticRows.length == 0}
              color="secondary"
              variant="contained" startIcon={<UnarchiveIcon />} >{t("exportArchive.btnExportArchive")}
            </Button>
            {downloadArchiveLoading && (
                <CircularProgress
                    size={24}
                    sx={{
                    color: blue[900],
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                    }}
                />
            )}
          </Box>
        </Stack>
      </Stack>
        
      <LogisticTransactionsDataGrid onSelectedRowsChange={handleSelectedLogisticRowsChange} onFilterModelChange={handleFilterModelChange} refreshData={logisticDataGridChanged} refreshDataGrid={handleRefreshLogisticDataGrid} getAllTransactions={true} /> {/* #POW-52 > #POW-80 */}
      <DialogCheckInTransaction open={open} onClose={handleClose} selectedRows={selectedLogisticRows} refreshDataGrid={handleRefreshLogisticDataGrid}/>  {/* #POW-21 > #POW-52*/}
      <Snackbar open={openAlert} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} autoHideDuration={8000} onClose={handleCloseSnackBar} sx={{maxWidth:'400px'}}>
        <Alert
            severity={alertSeverity}
            variant="filled"
            sx={{ width: '100%', marginTop: '50px' }}
            onClose={handleCloseSnackBar}
            >
          {alertMessage}
          </Alert>
      </Snackbar>  {/* #POW-100 */}
    </DashboardLayout>
  </>
  );
};

export default AllTransactionsPage;
